import Vue from 'vue';
import VueLS from 'vue-ls';
import VueCookies from 'vue-cookies';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useActions } from 'vuex-composition-helpers/dist';

Vue.use(VueLS, { namespace: 'qz__' });
Vue.use(VueCookies);

const AUTH_TOKEN_KEY = 'quotestation_token';
const SESSION_ID_KEY = 'PHPSESSID';
const PRODUCT_NAME = 'retrieve';

const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

class Api {
  constructor(options) {
    let accessToken;
    let phpSessId;

    this.authTokenKey = AUTH_TOKEN_KEY;
    this.sessionIdKey = SESSION_ID_KEY;

    if (process.env.NODE_ENV === 'production') {
      accessToken = Cookies.get(AUTH_TOKEN_KEY);
      phpSessId = Cookies.get(SESSION_ID_KEY);
    } else {
      accessToken = Vue.ls.get(this.authTokenKey);
      while (!accessToken) {
        // eslint-disable-next-line
        accessToken = prompt(`Insert ${this.authTokenKey}`);
      }
      Vue.ls.set(this.authTokenKey, accessToken);
      phpSessId = Vue.ls.get(this.sessionIdKey);
      while (!phpSessId) {
        // eslint-disable-next-line
        phpSessId = prompt(`Insert ${this.sessionIdKey}`);
      }
      Vue.ls.set(this.sessionIdKey, phpSessId);
    }

    this.accessToken = accessToken || null;
    this.phpSessId = phpSessId || null;
    this.productName = PRODUCT_NAME;

    this.client = options.client || axios.create({
      baseURL: options.apiBaseUrl,
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${this.accessToken}`,
      },
    });

    this.clientMicro = options.clientMicro || axios.create({
      baseURL: options.microBaseUrl,
      headers: {
        ...defaultHeaders,
        Authorization: `Bearer ${this.accessToken}`,
      },
    });

    this.clientQZMenus = options.clientMenus || axios.create({
      baseURL: options.wpApiUrl,
      headers: defaultHeaders,
    });

    this.clientCNIMenus = options.clientMenus || axios.create({
      baseURL: options.wpCniApiUrl,
      headers: defaultHeaders,
    });

    this.initResponseInterceptors();
  }

  initResponseInterceptors() {
    if (process.env.NODE_ENV === 'production') {
      this.client.interceptors.response.use(
        (r) => r,
        async (error) => {
          if (!error.response) {
            const { showToast } = useActions(['showToast']);

            showToast({
              text: 'Unexpected Network error',
            });

            return;
          }

          const { status, data } = error.response;

          switch (status) {
            case 440: {
              const redirectUrl = window.localStorage.getItem('initReq');

              if (redirectUrl) {
                window.location = redirectUrl;
              } else {
                const partnerResponse = await this.identifyPartner();
                window.location = partnerResponse.data;
              }
            }
              break;
            case 409:
              window.location = data.redirectTo;
              break;
            default:
              return Promise.reject(error);
          }
        },
      );
    }

    if (process.env.NODE_ENV === 'development') {
      this.client.interceptors.response.use(
        (r) => r,
        (error) => {
          const { status } = error.response;

          if (status === 440) {
            // eslint-disable-next-line
            const accessToken = prompt(`Insert ${this.authTokenKey}`);
            Vue.ls.set(this.authTokenKey, accessToken);

            // eslint-disable-next-line
            const phpSessId = prompt(`Insert ${this.sessionIdKey}`);
            Vue.ls.set(this.sessionIdKey, phpSessId);
          }

          return Promise.reject(error);
        },
      );
    }
  }

  async initForm() {
    const { data } = await this.client.post(
      'api/journey/initForm',
      { phpSessId: this.phpSessId, productName: this.productName },
    );
    return data;
  }

  async identifyPartner (url = window.location.href) {
    const { data } = await this.client.post('api/journey/identifyPartner', {
      url,
      product: this.productName,
    });

    return data;
  }

  async login(credentials) {
    const path = 'api/microservice/userLogin/v2';
    const { data } = await this.clientMicro.post(
      path,
      {
        ...credentials,
        phpSessId: this.phpSessId,
      },
      {
        validateStatus(status) {
          return (status >= 200 && status < 300) || status === 401 || status === 429;
        },
      },
    );
    return data;
  }

  async getCookiesToSet() {
    const { data } = await this.clientMicro.post(
      '/api/journey/cookiesToSet',
      {
        phpSessId: this.phpSessId,
        productName: this.productName,
      },
    );
    return data;
  }

  async getTracking ({ step }) {
    const { data } = await this.client.post(
      'api/journey/trackers',
      {
        step,
        productName: this.productName,
        phpSessId: this.phpSessId,
      },
    );
    return data;
  }

  async forgotPassword(credentials) {
    const path = 'api/micro/forgotPassword/v2';
    const { data } = await this.clientMicro.post(
      path,
      credentials,
      {
        validateStatus(status) {
          return (status >= 200 && status < 300) || status === 401;
        },
      },
    );
    return data;
  }

  async changePassword(credentials) {
    const path = 'api/micro/changePassword/v2';
    const { data } = await this.clientMicro.post(
      path,
      credentials,
      {
        validateStatus(status) {
          return (status >= 200 && status < 300) || status === 401;
        },
      },
    );
    return data;
  }

  async resetPassword(credentials) {
    const path = 'api/micro/resetPassword/v2';
    const { data } = await this.clientMicro.post(
      path,
      {
        ...credentials,
        phpSessId: this.phpSessId,
      },
      {
        validateStatus(status) {
          return (status >= 200 && status < 300) || status === 401;
        },
      },
    );
    return data;
  }

  async getQuotes(payload) {
    const path = 'api/retrieve/v2';

    const { memberId, ...payloadWithoutMemberId } = payload;

    const { data } = await this.clientMicro.post(path, {
      ...(memberId ? payload : payloadWithoutMemberId),
      phpSessId: this.phpSessId,
    });

    return data;
  }

  async deleteQuote(logId) {
    const path = 'api/retrieveDeleteQuote';
    const { data } = await this.clientMicro.post(path, { logId });
    return data;
  }

  async getRewards({ email, userIds, source }) {
    const path = 'api/rewards/v2';
    const { data } = await this.client.post(path, { email, userIds, source });
    return data;
  }

  async processRewardsLinkClick(payload) {
    const path = 'api/rewards/promotion';
    const { data } = await this.client.post(path, payload);
    return data;
  }

  async getMenu(slug, forQZPartner = true) {
    const path = `menus/v1/menus/${slug}`;
    const clientMenus = forQZPartner ? this.clientQZMenus : this.clientCNIMenus;

    const { data } = await clientMenus.get(path);
    return data.items.map((item) => ({
      title: item.title,
      url: item.url,
      childItems: item.child_items ? item.child_items.map((childItem) => ({
        title: childItem.title,
        url: childItem.url,
      })) : null,
    }));
  }
}

export default new Api({
  apiBaseUrl: process.env.VUE_APP_QUOTESTATION_API_URL,
  microBaseUrl: process.env.VUE_APP_MICRO_API_URL,
  wpApiUrl: process.env.VUE_APP_WP_API_URL,
  wpCniApiUrl: process.env.VUE_APP_WP_CNI_API_URL,
});
